import React, { useCallback, useEffect, useMemo } from 'react';

import { Grid } from '@mui/material';
import { AccountStatus, OrderService } from '../../api';
import { MULTIPLE_TICKETS_DETAILS_FIELD_NAME, quotePrice } from '../core/pricing';
import { PluginType } from '../PluginUtils';
import { WidgetThemeProvider } from '../theme/Theme';
import { deepOmitNull } from '../utils';
import RotatingLoader from '../../ui/RotatingLoader';
import { LoaderLogoProvider } from '../../ui/LoaderLogo';
import { OrderPageContext } from './useOrderPage';
import { useInitOrderPageState } from './initOrderPageState';
import { setOrderPageStateToLocalStorage } from './orderPageStateLocalStorage';
import { useOrderPageProviderBase } from './useOrderPageProviderBase';
import { AccountNotActive } from '../../shared/ui/AccountNotActive';

export function pluginEnabled(orderCustomDetail, pluginName) {
  const item = orderCustomDetail[pluginName] === true;
  return !!item;
}

const defaultTheme = {
  palette: {
    primary: '#4abd00',
    trackFill: 'rgba(74,189,0,0.63)',
    railFill: 'rgba(74,189,0,0.63)',
    handle: '#1e6203',
    text: {
      button: {
        outlined: '#4abd00',
      },
    },
    border: '#4abd00',
    button: {
      outlined: '#4abd00',
    },
  },
};

/**
 * @param {{
 *   children: any,
 *   storefrontTheme?: any,
 *   orderPageIdOverride?: string,
 *   startsMonetaryGiftCard?: boolean,
 * }} props
 */
export const OrderPageProvider = ({ children, storefrontTheme, orderPageIdOverride, startsMonetaryGiftCard }) => {
  const {
    orderPageState,
    setOrderPageState,
    shouldUseDefaultTheme,
    isLoadingLanguage,
    setPersonalDetails,
    setPaymentUrl,
    setTip,
    setActiveStep,
    setActiveStepAndOrderPageIntent,
    disclaimersAccepted,
    disclaimersError,
    setDisclaimersError,
    onDisclaimersChange,
    sessionTtlMilliseconds,
    setSessionTtlMilliseconds,
  } = useOrderPageProviderBase();

  const noMinDate = new URLSearchParams(window.location.search).get('nomindate') === 'true';
  useInitOrderPageState({ orderPageState, setOrderPageState, orderPageIdOverride, startsMonetaryGiftCard });

  // Sync state to localStorage whenever it changes
  useEffect(() => {
    if (orderPageState.isInitialized && orderPageState.orderPage?.id) {
      setOrderPageStateToLocalStorage(orderPageState.orderPage.id, orderPageState);
    }
  }, [orderPageState]);

  useEffect(() => {
    void (async () => {
      if (orderPageState.isLoadingDates && orderPageState.account) {
        const response = await OrderService.getAvailableDates({
          account_id: orderPageState.account.id,
          order_page_id: orderPageState.orderPage.id,
          order_details: orderPageState.details,
          no_minimum_date: noMinDate,
        });
        setOrderPageState((prev) => ({ ...prev, isLoadingDates: false, availableDates: response }));
      }
    })();
  }, [orderPageState.isLoadingDates, orderPageState.account]);

  const setOrderDetailValue = useCallback((dataType, value) => {
    setOrderPageState((prev) => {
      const tempDetails = {
        ...prev.details,
        [dataType]: value,
      };
      let shouldUseweekendPlugin = false;
      if (pluginEnabled(prev.details?.custom_details, PluginType.WEEKEND_AND_ISRAELI_HOLIDAYS)) {
        shouldUseweekendPlugin = true;
      }
      const { total: price } = quotePrice({
        orderPage: prev.orderPage,
        details: tempDetails,
        dateDetails: prev.dateAndTimeDetails,
        account: prev.account,
        forceWeekendAddition: shouldUseweekendPlugin,
      });

      return {
        ...prev,
        isLoadingDates: true,
        availableDates: [],
        details: {
          ...tempDetails,
          price,
        },
      };
    });
  }, []);

  const setCustomOrderDetailValue = useCallback((name, value) => {
    setOrderPageState((prev) => {
      const tempDetails = {
        ...prev.details,
        custom_details: {
          ...prev.details.custom_details,
          [name]: value,
        },
      };
      let shouldUseweekendPlugin = false;
      if (pluginEnabled(tempDetails.custom_details, PluginType.WEEKEND_AND_ISRAELI_HOLIDAYS)) {
        shouldUseweekendPlugin = true;
      }
      const { total: price } = quotePrice({
        orderPage: prev.orderPage,
        details: tempDetails,
        dateDetails: prev.dateAndTimeDetails,
        account: prev.account,
        forceWeekendAddition: shouldUseweekendPlugin,
      });

      return {
        ...prev,
        isLoadingDates: true,
        availableDates: [],
        details: {
          ...tempDetails,
          price,
        },
      };
    });
  }, []);

  const setMultipleTicketsDetails = useCallback((multipleTicketsDetails) => {
    setOrderPageState((prev) => {
      const totalPersons = Object.values(multipleTicketsDetails).reduce((sum, val) => sum + val, 0);
      const tempDetails = {
        ...prev.details,
        persons: totalPersons,
        custom_details: {
          ...prev.details.custom_details,
          [MULTIPLE_TICKETS_DETAILS_FIELD_NAME]: {
            ...prev.details.custom_details.multiple_tickets_details,
            ...multipleTicketsDetails,
          },
        },
      };
      let shouldUseweekendPlugin = false;
      if (pluginEnabled(prev.details.custom_details, PluginType.WEEKEND_AND_ISRAELI_HOLIDAYS)) {
        shouldUseweekendPlugin = true;
      }
      const { total: price } = quotePrice({
        orderPage: prev.orderPage,
        details: tempDetails,
        dateDetails: prev.dateAndTimeDetails,
        account: prev.account,
        forceWeekendAddition: shouldUseweekendPlugin,
      });

      return {
        ...prev,
        isLoadingDates: true,
        availableDates: [],
        details: {
          ...tempDetails,
          price,
        },
      };
    });
  }, []);

  const setOrderCustomDetails = useCallback((customDetails) => {
    setOrderPageState((prev) => {
      const tempDetails = {
        ...prev.details,
        custom_details: {
          ...prev.details.custom_details,
          ...customDetails,
        },
      };
      let shouldUseweekendPlugin = false;
      if (pluginEnabled(tempDetails.custom_details, PluginType.WEEKEND_AND_ISRAELI_HOLIDAYS)) {
        shouldUseweekendPlugin = true;
      }
      const { total: price } = quotePrice({
        orderPage: prev.orderPage,
        details: tempDetails,
        dateDetails: prev.dateAndTimeDetails,
        account: prev.account,
        forceWeekendAddition: shouldUseweekendPlugin,
      });

      return {
        ...prev,
        isLoadingDates: true,
        availableDates: [],
        details: {
          ...tempDetails,
          price,
        },
      };
    });
  }, []);

  const setDate = useCallback((date) => {
    setOrderPageState((prev) => {
      const tempDateDetails = {
        ...prev.dateAndTimeDetails,
        date,
      };
      let shouldUseweekendPlugin = false;
      if (pluginEnabled(prev.details?.custom_details, PluginType.WEEKEND_AND_ISRAELI_HOLIDAYS)) {
        shouldUseweekendPlugin = true;
      }
      const { total: price } = quotePrice({
        orderPage: prev.orderPage,
        details: prev.details,
        dateDetails: tempDateDetails,
        account: prev.account,
        forceWeekendAddition: shouldUseweekendPlugin,
      });

      return {
        ...prev,
        dateAndTimeDetails: {
          ...prev.dateAndTimeDetails,
          date,
        },
        details: {
          ...prev.details,
          price,
        },
      };
    });
  }, []);

  const setTime = useCallback((time, courseId) => {
    setOrderPageState((prev) => ({
      ...prev,
      dateAndTimeDetails: {
        ...prev.dateAndTimeDetails,
        time,
        courseId,
      },
    }));
  }, []);

  const reloadDates = useCallback(() => {
    setOrderPageState((prev) => ({ ...prev, isLoadingDates: true }));
  }, []);

  const ctx = useMemo(
    () => ({
      reloadDates,
      orderPageState,
      setOrderDetailValue,
      setCustomOrderDetailValue,
      setOrderCustomDetails,
      setMultipleTicketsDetails,
      setOrderPageState,
      setPersonalDetails,
      setDate,
      setTime,
      setPaymentUrl,
      setActiveStep,
      setActiveStepAndOrderPageIntent,
      setTip,
      disclaimersAccepted,
      disclaimersError,
      setDisclaimersError,
      onDisclaimersChange,
      sessionTtlMilliseconds,
      setSessionTtlMilliseconds,
    }),
    [
      reloadDates,
      orderPageState,
      setOrderDetailValue,
      setCustomOrderDetailValue,
      setOrderCustomDetails,
      setMultipleTicketsDetails,
      setOrderPageState,
      setPersonalDetails,
      setDate,
      setTime,
      setPaymentUrl,
      setActiveStep,
      setActiveStepAndOrderPageIntent,
      setTip,
      disclaimersAccepted,
      disclaimersError,
      setDisclaimersError,
      onDisclaimersChange,
      sessionTtlMilliseconds,
      setSessionTtlMilliseconds,
    ],
  );
  if (!orderPageState.isInitialized || isLoadingLanguage) {
    return (
      <Grid container minHeight={300}>
        <RotatingLoader />
      </Grid>
    );
  }

  if (orderPageState.account?.status === AccountStatus.PAUSED) {
    return <AccountNotActive />;
  }
  const iframeHeight = document.body.offsetHeight;
  const iframeWidth = document.body.offsetWidth;

  window.parent.postMessage({ height: iframeHeight, width: iframeWidth }, '*');

  const externalTheme = storefrontTheme || deepOmitNull(orderPageState.account.custom_theme);
  return (
    <LoaderLogoProvider account={orderPageState.account}>
      <WidgetThemeProvider remoteThemeOverride={shouldUseDefaultTheme ? defaultTheme : externalTheme}>
        <OrderPageContext.Provider value={ctx}>{children}</OrderPageContext.Provider>
      </WidgetThemeProvider>
    </LoaderLogoProvider>
  );
};
